'use client'
import {Link} from "@/i18n/routing";
import {useLocale, useTranslations} from 'next-intl';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';
import {useEffect, useState} from 'react';
import {loginAction} from '@/actions/auth';
import {useErrorAlert} from '@/hooks/useSweetAlert';
import {getGqlErrorToast} from '@/libs/api/errorHandler';
import {
  CALLBACK_URL_PARAM,
  CLIENT_BANNED_MESSAGE,
  PRIVATE_ROUTE_NAME,
  UNAUTHORIZED_MESSAGE,
  UNAUTHORIZED_URL_PARAM
} from '@/libs/constants/common.constants';
import {redirect} from '@/i18n/routing';
import {SERVER_URL} from '@/libs/constants/url.constants';
import {ZodValidator} from '@/libs/zod.validator';
import {useSearchParams} from 'next/navigation';
import {useUserContext} from '@/providers/UserContext';
import {useCartContext} from '@/providers/CartContext';
import {parseCartProducts, parseWishlistProducts} from '@/libs/product';
import {useWishlistContext} from '@/providers/WshlistContext';

export default function LoginPrimary({ currency }) {
  const e = useTranslations('error.message');
  const v = useTranslations('validators');
  const t = useTranslations('auth');
  
  const query = useSearchParams();
  const callbackUrl = query.get(CALLBACK_URL_PARAM) ?? PRIVATE_ROUTE_NAME;
  const unauthorized = query.get(UNAUTHORIZED_URL_PARAM);
  const { saveUser, deleteUser } = useUserContext();
  const { cartProducts, updateCartLocalStorage } = useCartContext();
  const { wishlistProducts, updateWishlistLocalStorage } = useWishlistContext();
  
  const [passwordType, setPasswordType] = useState('password');
  const [passwordIcon, setPasswordIcon] = useState('eye-slash');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const toast = useErrorAlert();
  const locale = useLocale();
  
  useEffect(() => {
    if (unauthorized === CLIENT_BANNED_MESSAGE || unauthorized === UNAUTHORIZED_MESSAGE) {
      deleteUser();
      if (unauthorized === CLIENT_BANNED_MESSAGE) {
        toast(getGqlErrorToast(unauthorized, e));
      }
    }
  }, [unauthorized]);
  
  const handleTogglePassword = () => {
    if (passwordType === 'password'){
      setPasswordIcon('eye');
      setPasswordType('text')
    } else {
      setPasswordIcon('eye-slash')
      setPasswordType('password')
    }
  }
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(
        z.object({
          email: ZodValidator.email(v, 3),
          password: ZodValidator.password(v, 8)
        })
    ),
    defaultValues: {
      email: '',
      password: ''
    }
  });
  
  const onLogin = async (data) => {
    setLoading(true);
    const result = await loginAction({
      ...data,
      locale,
      currency,
      cart: cartProducts?.map(cp => ({
        productId: cp.product.id,
        quantity: cp.quantity
      })) ?? [],
      wishlist: wishlistProducts?.map(wp => ({
        productId: wp.product.id
      })) ?? []
    });
    
    if (result.error) {
      toast(getGqlErrorToast(result.error.errors, e));
      setLoading(false);
      return;
    }
    if (result.data) {
      saveUser(result.data);
      
      const {freshData, totalPrice} = parseCartProducts(result.cart, null);
      updateCartLocalStorage(freshData, totalPrice);
      const {freshData: freshWishlist} = parseWishlistProducts(result.wishlist);
      updateWishlistLocalStorage(freshWishlist);
      
      // bug on second login, accessToken is undefined in middleware even it's saved in loginAction
      redirect({
        href: callbackUrl,
        locale
      });
      setLoading(false);
    }
  };
  
  const onGoogleAuth = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setGoogleLoading(true);
    window.location.href = `${SERVER_URL}/auth/google?locale=${locale}&${CALLBACK_URL_PARAM}=${callbackUrl}&currency=${currency}`;
    setLoading(false);
    setGoogleLoading(false);
  };
  
  return (
    <div className="ltn__login-area pb-65">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="section-title-area text-center">
              <h1 className="section-title">
                {t('loginTitle')}
              </h1>
              <p>
                {t('loginDescription')}
              </p>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6">
            <div className="account-login-inner">
              <form onSubmit={handleSubmit(onLogin)} className="ltn__form-box contact-form-box">
                <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                  <input
                      id="email"
                      type="email"
                      placeholder={`${t('email')}*`}
                      {...register('email')}
                  />
                  {errors.email && <span className="error-message">{errors.email.message}</span>}
                </div>
                <div className={`form-group ${errors.password ? 'has-error' : ''}`}>
                  <div className="password-field">
                    <input
                        id="password"
                        type={passwordType}
                        placeholder={`${t('password')}*`}
                        {...register('password')}
                    />
                    <span className="icon-wrapper" onClick={handleTogglePassword}>
                      <i className={`fa-regular fa-${passwordIcon}`}></i>
                    </span>
                  </div>
                  {errors.password && <span className="error-message">{errors.password.message}</span>}
                </div>
                <div className="btn-wrapper mt-0">
                  <button
                      className="theme-btn-1 btn btn-block w-100 text-uppercase"
                      type="submit"
                      disabled={isSubmitting || loading || googleLoading}
                  >
                    {isSubmitting || loading ? t('signingIn') : t('signIn')}
                  </button>
                </div>
                <div className="button-divider">
                  <hr/>
                  <span>{t('or')}</span>
                  <hr/>
                </div>
                <div className="btn-wrapper mt-0">
                  <button
                      className="google-button btn btn-block w-100 text-uppercase"
                      onClick={(e) => onGoogleAuth(e)}
                      disabled={googleLoading || loading}
                  >
                    <i className="fa-brands fa-google me-3"></i>
                    {googleLoading ? t('signingIn') : t('signInGoogle')}
                  </button>
                </div>
                <div className="go-to-btn mt-20 text-center">
                  <Link href={`/forgot-password?${CALLBACK_URL_PARAM}=${callbackUrl}`}>
                  <small className="text-uppercase">{t('forgotPassword')}</small>
                  </Link>
                </div>
              </form>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="account-create text-center pt-50">
              <h4 className="text-uppercase">{t('preRegisterTitle')}</h4>
              <p>
                {t.rich('preRegisterDescription', {
                  br: () => (<br/>),
                })}
              </p>
              <div className="btn-wrapper">
                <Link href={`/register?${CALLBACK_URL_PARAM}=${callbackUrl}`} className="theme-btn-1 btn black-btn text-uppercase">
                  {t('createAccount')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
